import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { Search, Check, X, AlertCircle, FileText, Users, ChevronLeft, ChevronRight, Ban } from "lucide-react";
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';
import adminApi from '../../../api/adminApi';
import '../../../assets/css/admin/therapistmanagement.css';


const TherapistManagement = () => {
  const location = useLocation();
  const [therapists, setTherapists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(location.state?.search || "");

  // Coming from the header's global search — apply the term it passed along
  useEffect(() => {
    if (location.state?.search) {
      setSearchTerm(location.state.search);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);
  const [activeTab, setActiveTab] = useState("All");
  const [selectedTherapist, setSelectedTherapist] = useState(null);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 5;

  // ── Map backend kyc_status to display status ─────────────
  const mapStatus = (kycStatus) => {
    switch (kycStatus) {
      case "approved": return "Approved";
      case "rejected": return "Rejected";
      case "blocked": return "Blocked";
      case "pending": return "Pending";
      default: return "Pending";
    }
  };

  // ── Fetch list from backend ───────────────────────────────
  const fetchTherapists = () => {
    setLoading(true);
    adminApi.get('/kyc/list')
      .then((res) => {
        const mapped = res.data.data.map((t) => ({
          id: t.id,
          name: t.full_name,
          email: t.email,
          status: mapStatus(t.kyc_status),
          attempts: t.kyc_attempts,
        }));
        setTherapists(mapped);
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchTherapists();
  }, []);

  // ── Fetch single therapist detail when selected ───────────
  const handleSelectTherapist = (therapist) => {
    setSelectedTherapist(therapist);
    setDetailLoading(true);
    setSelectedDetail(null);
    adminApi.get(`/kyc/${therapist.id}`)
      .then((res) => {
        setSelectedDetail(res.data);
        setDetailLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setDetailLoading(false);
        Swal.fire({ icon: "error", title: "Failed to load", text: "Could not fetch therapist details." });
      });
  };

  const filteredTherapists = therapists
    .filter(t => activeTab === "All" || t.status === activeTab)
    .filter(t =>
      t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.email.toLowerCase().includes(searchTerm.toLowerCase())
    );

  const totalPages = Math.ceil(filteredTherapists.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedTherapists = filteredTherapists.slice(startIndex, startIndex + itemsPerPage);

  const stats = {
    total: therapists.length,
    active: therapists.filter(t => t.status === "Approved").length,
    pending: therapists.filter(t => t.status === "Pending").length,
    inactive: therapists.filter(t => t.status === "Rejected" || t.status === "Blocked").length
  };

  const handleApprove = (therapist) => {
    Swal.fire({
      title: 'Approve Therapist?',
      html: `Set commission percentage for <strong>${therapist.name}</strong> (10% - 15%)`,
      icon: 'question',
      input: 'number',
      inputLabel: 'Commission Percentage (%)',
      inputValue: 12,
      inputAttributes: {
        min: 10,
        max: 15,
        step: 1
      },
      showCancelButton: true,
      confirmButtonText: 'Yes, Approve',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#16a34a',
      cancelButtonColor: '#dc2626',
      inputValidator: (value) => {
        if (!value) {
          return 'Commission percentage is required';
        }
        const num = Number(value);
        if (!Number.isInteger(num)) {
          return 'Commission must be a whole number';
        }
        if (num < 10 || num > 15) {
          return 'Commission must be between 10% and 15%';
        }
      }
    }).then((result) => {
      if (result.isConfirmed) {
        adminApi.post(`/kyc/${therapist.id}/approve`, {
          commission_percentage: Number(result.value)
        })
          .then(() => {
            Swal.fire({
              title: 'Approved!',
              html: `<strong>${therapist.name}</strong> has been approved successfully with ${result.value}% commission.`,
              icon: 'success',
              confirmButtonText: 'OK',
              confirmButtonColor: '#16a34a',
              timer: 3000
            });
            fetchTherapists();
            setSelectedTherapist(null);
            setSelectedDetail(null);
          })
          .catch((err) => {
            Swal.fire({ icon: 'error', title: 'Failed', text: err?.response?.data?.message || 'Could not approve therapist.' });
          });
      }
    });
  };

  const handleReject = (therapist) => {
    Swal.fire({
      title: 'Reject Therapist?',
      html: `Are you sure you want to reject <strong>${therapist.name}</strong>?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, Reject',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#64748b',
      input: 'textarea',
      inputPlaceholder: 'Reason for rejection (required)',
      inputValidator: (value) => {
        if (!value) return 'Please provide a reason for rejection';
      }
    }).then((result) => {
      if (result.isConfirmed) {
        adminApi.post(`/kyc/${therapist.id}/reject`, { reason: result.value })
          .then((res) => {
            Swal.fire({
              title: 'Rejected!',
              html: res.data.message,
              icon: 'error',
              confirmButtonText: 'OK',
              confirmButtonColor: '#dc2626',
              timer: 3000
            });
            fetchTherapists();
            setSelectedTherapist(null);
            setSelectedDetail(null);
          })
          .catch((err) => {
            Swal.fire({ icon: 'error', title: 'Failed', text: err?.response?.data?.message || 'Could not reject therapist.' });
          });
      }
    });
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <div className="tm-wrapper">
      <div className="tm-container">
        {/* Header */}
        <div className="tm-header">
          <h1 className="tm-title">Therapist Management</h1>
          <p className="tm-subtitle">Manage therapist profiles, approvals & availability</p>
        </div>

        {/* Stats Cards */}
        <div className="tm-stats-grid">
          <div className="tm-stat-card">
            <div className="tm-stat-icon tm-stat-navy">
              <Users size={24} />
            </div>
            <div>
              <div className="tm-stat-value">{stats.total}</div>
              <div className="tm-stat-label">Total Therapists</div>
            </div>
          </div>
          <div className="tm-stat-card">
            <div className="tm-stat-icon tm-stat-success">
              <Check size={24} />
            </div>
            <div>
              <div className="tm-stat-value">{stats.active}</div>
              <div className="tm-stat-label">Active</div>
            </div>
          </div>
          <div className="tm-stat-card">
            <div className="tm-stat-icon tm-stat-orange">
              <AlertCircle size={24} />
            </div>
            <div>
              <div className="tm-stat-value">{stats.pending}</div>
              <div className="tm-stat-label">Pending Approval</div>
            </div>
          </div>
          <div className="tm-stat-card">
            <div className="tm-stat-icon tm-stat-danger">
              <X size={24} />
            </div>
            <div>
              <div className="tm-stat-value">{stats.inactive}</div>
              <div className="tm-stat-label">Rejected / Blocked</div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="tm-main-card">
          {/* Tabs */}
          <div className="tm-tabs">
            {["All", "Pending", "Approved", "Rejected", "Blocked"].map(tab => (
              <button
                key={tab}
                className={`tm-tab ${activeTab === tab ? "tm-tab-active" : ""}`}
                onClick={() => {
                  setActiveTab(tab);
                  setCurrentPage(1);
                }}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Filters */}
          <div className="tm-filters">
            <span className="tm-filter-label">Search:</span>
            <div className="tm-search-wrapper">
              <Search size={18} className="tm-search-icon" />
              <input
                type="text"
                placeholder="Search by name or email"
                className="tm-search-input"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>
          </div>

          {/* Content Grid */}
          <div className="tm-content-grid">
            {/* Left: Therapist List */}
            <div className="tm-list-section">
              <h3 className="tm-section-title">Therapists List</h3>

              {loading ? (
                <div className="tm-empty-state">
                  <p>Loading...</p>
                </div>
              ) : filteredTherapists.length === 0 ? (
                <div className="tm-empty-state">
                  <AlertCircle size={48} />
                  <p>No therapists found</p>
                </div>
              ) : (
                <>
                  <div className="tm-table-container">
                    <table className="tm-table">
                      <thead>
                        <tr>
                          <th className="tm-th">Name</th>
                          <th className="tm-th">Email</th>
                          <th className="tm-th">Status</th>
                          <th className="tm-th">Attempts</th>
                          <th className="tm-th">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedTherapists.map(therapist => (
                          <tr key={therapist.id} className="tm-tr">
                            <td className="tm-td">{therapist.name}</td>
                            <td className="tm-td">{therapist.email}</td>
                            <td className="tm-td">
                              <span className={`tm-status-badge ${
                                therapist.status === "Approved" ? "tm-status-approved" :
                                therapist.status === "Pending" ? "tm-status-pending" :
                                therapist.status === "Rejected" ? "tm-status-rejected" :
                                "tm-status-blocked"
                              }`}>
                                {therapist.status}
                              </span>
                            </td>
                            <td className="tm-td">{therapist.attempts}/3</td>
                            <td className="tm-td">
                              <button
                                className="tm-btn-view"
                                onClick={() => handleSelectTherapist(therapist)}
                              >
                                View Profile
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  <div className="tm-pagination">
                    <div className="tm-pagination-info">
                      Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, filteredTherapists.length)} of {filteredTherapists.length} entries
                    </div>
                    <div className="tm-pagination-controls">
                      <button
                        className="tm-pagination-btn"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        <ChevronLeft size={18} />
                      </button>

                      {[...Array(totalPages)].map((_, index) => {
                        const page = index + 1;
                        return (
                          <button
                            key={page}
                            className={`tm-pagination-number ${currentPage === page ? "tm-pagination-number-active" : ""}`}
                            onClick={() => handlePageChange(page)}
                          >
                            {page}
                          </button>
                        );
                      })}

                      <button
                        className="tm-pagination-btn"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        <ChevronRight size={18} />
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>

            {/* Right: Details Panel */}
            <div className="tm-details-panel">
              {detailLoading ? (
                <div className="tm-no-selection">
                  <p>Loading details...</p>
                </div>
              ) : selectedDetail ? (
                <div className="tm-details-content">
                  <h2 className="tm-details-title">{selectedDetail.kyc.full_name}</h2>

                  <div className="tm-detail-row">
                    <span className="tm-detail-label">Registered Email:</span>
                    <span className="tm-detail-value">{selectedDetail.therapist.email}</span>
                  </div>

                  <div className="tm-detail-row">
                    <span className="tm-detail-label">KYC Email:</span>
                    <span className="tm-detail-value">{selectedDetail.kyc.email}</span>
                  </div>

                  <div className="tm-detail-row">
                    <span className="tm-detail-label">Phone:</span>
                    <span className="tm-detail-value">{selectedDetail.kyc.phone}</span>
                  </div>

                  <div className="tm-detail-row">
                    <span className="tm-detail-label">CNIC:</span>
                    <span className="tm-detail-value">{selectedDetail.kyc.cnic}</span>
                  </div>

                  <div className="tm-detail-row">
                    <span className="tm-detail-label">Specialization:</span>
                    <span className="tm-detail-value">{selectedDetail.kyc.specialization}</span>
                  </div>

                  <div className="tm-detail-row">
                    <span className="tm-detail-label">Qualification:</span>
                    <span className="tm-detail-value">{selectedDetail.kyc.qualification}</span>
                  </div>

                  <div className="tm-detail-row">
                    <span className="tm-detail-label">Institution:</span>
                    <span className="tm-detail-value">{selectedDetail.kyc.institution || "N/A"}</span>
                  </div>

                  <div className="tm-detail-row">
                    <span className="tm-detail-label">Experience:</span>
                    <span className="tm-detail-value">{selectedDetail.kyc.experience} years</span>
                  </div>

                  <div className="tm-detail-row">
                    <span className="tm-detail-label">License No:</span>
                    <span className="tm-detail-value">{selectedDetail.kyc.license_number}</span>
                  </div>

                  <div className="tm-detail-row">
                    <span className="tm-detail-label">Attempts:</span>
                    <span className="tm-detail-value">{selectedDetail.therapist.kyc_attempts}/3</span>
                  </div>

                  {selectedDetail.kyc.rejection_reason && (
                    <div className="tm-detail-row">
                      <span className="tm-detail-label">Last Rejection Reason:</span>
                      <span className="tm-detail-value">{selectedDetail.kyc.rejection_reason}</span>
                    </div>
                  )}

                  <div className="tm-documents-section">
                    <h3 className="tm-documents-title">Documents</h3>
                   <div className="tm-document-item">
  <div className="tm-document-info">
    <FileText size={18} className="tm-document-icon" />
    <span>KYC Documents.pdf</span>
  </div>
  <a
    href={selectedDetail.document_url}
    target="_blank"
    rel="noopener noreferrer"
    className="tm-btn-view-doc"
  >
    View
  </a>
</div>
                  </div>

                  {(selectedTherapist.status === "Pending") && (
                    <div className="tm-action-buttons">
                      <button
                        className="tm-btn tm-btn-approve"
                        onClick={() => handleApprove(selectedTherapist)}
                      >
                        Approve
                      </button>
                      <button
                        className="tm-btn tm-btn-reject"
                        onClick={() => handleReject(selectedTherapist)}
                      >
                        Reject
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="tm-no-selection">
                  <AlertCircle size={48} />
                  <p>Select a therapist to view details</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TherapistManagement;